import _ from 'underscore';
import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Panel, PanelType } from '@fluentui/react';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useCopilotRepositoryContext } from '@bingads-webui/inline-copilot';
import { ViewAllRecommendations } from '@bingads-webui/inline-copilot-ui';
import { RecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';

import { getAdsInMatchingGroup, getItemsForListFromGroupedAds } from '../model/constants';


export const AllRecommendationsPanel = ({ isOpen, onDismiss, onSelectImageSuggestion }) => {
  const i18n = useContext(I18nContext);
  const activity = useContext(ActivityContext);

  const [{ isLoading, groupedAds } = {}] = useContext(RecommendationContext);
  const { finalUrl } = useCopilotRepositoryContext();

  const images = useMemo(() => getItemsForListFromGroupedAds(groupedAds), [groupedAds]);

  const handleDismiss = useCallback(() => {
    if (activity) {
      activity.signal('[All Recommendations Panel] - Closing panel');
    }

    onDismiss();
  }, [activity, onDismiss]);

  const handleSelectImageSuggestion = useCallback((id) => {
    if (activity) {
      activity.signal(`[All Recommendations Panel] - Selecting image ${id}`);
    }

    const matchingAds = getAdsInMatchingGroup(groupedAds, id);

    onSelectImageSuggestion(matchingAds);
    onDismiss();
  }, [activity, groupedAds, onSelectImageSuggestion, onDismiss]);

  return (
    <Panel
      styles={{
        content: {
          height: 'calc(100% - 64px)',
        },
        scrollableContent: {
          height: '100%',
        },
        main: {
          animation: 'none',
        },
      }}
      type={PanelType.custom}
      customWidth="960px"
      closeButtonAriaLabel={i18n.getString('Close')}
      headerText={i18n.getString(_TL_('Recommended creatives'))}
      isOpen={isOpen}
      onDismiss={handleDismiss}
    >
      <FluentProvider theme={webLightTheme} style={{ height: '100%' }}>
        <ViewAllRecommendations
          creatives={images}
          finalUrl={finalUrl}
          isLoading={isLoading}
          onFinalUrlEntered={_.noop}
          onCreativeSelected={handleSelectImageSuggestion}
        />
      </FluentProvider>
    </Panel>
  );
};

AllRecommendationsPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onSelectImageSuggestion: PropTypes.func.isRequired,
};
